import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { StarInput } from "@/components/Stars";
import { MediaUploader } from "@/components/Media";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const Done = ({ title, text }) => (
  <div className="max-w-xl mx-auto px-5 py-24 text-center fade-up" data-testid="review-form-done">
    <CheckCircle2 size={56} className="mx-auto text-emerald-700" />
    <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight">{title}</h1>
    <p className="mt-3 text-gray-600">{text}</p>
    <Link to="/" className="mt-8 inline-block text-emerald-900 font-semibold hover:underline" data-testid="done-see-reviews">Vedi le recensioni →</Link>
  </div>
);

const ProductRating = ({ p, value, onChange, onMedia, token }) => (
  <div className="border border-gray-200 rounded-lg p-5 bg-white" data-testid={`product-rating-${p.product_id}`}>
    <div className="flex items-center gap-4">
      {p.image && <img src={p.image} alt="" className="h-14 w-14 rounded-md object-cover border border-gray-200" />}
      <p className="font-semibold">{p.name}</p>
    </div>
    <div className="mt-4"><StarInput size={26} value={value.rating} onChange={(rating) => onChange({ ...value, rating })} testid={`product-stars-${p.product_id}`} /></div>
    <Textarea className="mt-3" rows={2} maxLength={2000} placeholder="Cosa ne pensi di questo prodotto? (facoltativo)" value={value.comment}
      onChange={(e) => onChange({ ...value, comment: e.target.value })} data-testid={`product-comment-${p.product_id}`} />
    <div className="mt-3"><MediaUploader token={token} items={value.media || []} testid={`product-media-${p.product_id}`}
      onChange={onMedia} /></div>
  </div>
);

export default function ReviewForm() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ rating: 0, title: "", comment: "", author_name: "", website: "" });
  const [prod, setProd] = useState({});
  const [media, setMedia] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/invite/${token}`).then(({ data }) => {
      setInv(data);
      setForm((f) => ({ ...f, author_name: data.customer_name }));
      setProd(Object.fromEntries(data.products.map((p) => [p.product_id, { rating: 0, comment: "" }])));
    }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Seleziona un voto per il negozio");
    if (form.comment.trim().length < 10) return toast.error("Scrivi almeno 10 caratteri");
    setSending(true);
    try {
      const products = Object.entries(prod).filter(([, v]) => v.rating)
        .map(([product_id, v]) => ({ product_id, rating: v.rating, comment: v.comment, media_ids: (v.media || []).map((m) => m.id) }));
      await api.post(`/public/invite/${token}/review`, { ...form, products, media_ids: media.map((m) => m.id) });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  if (error) return <PublicShell><Done title="Link non valido" text={error} /></PublicShell>;
  if (!inv) return <PublicShell><div className="h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div></PublicShell>;
  if (done) return <PublicShell><Done title="Grazie per la tua recensione!" text="La tua opinione è stata inviata e sarà pubblicata dopo una breve verifica." /></PublicShell>;
  if (inv.completed) return <PublicShell><Done title="Recensione già inviata" text="Hai già valutato questo ordine. Grazie!" /></PublicShell>;

  return (
    <PublicShell>
      <form onSubmit={submit} className="max-w-2xl mx-auto px-5 sm:px-8 py-14 space-y-10" data-testid="review-form">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-900 font-semibold">Ordine #{inv.order_number}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Ciao {inv.customer_name}, com'è andata?</h1>
          <p className="mt-3 text-gray-600">La tua recensione verrà pubblicata con il badge "Acquisto verificato".</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 space-y-6">
          <div>
            <Label className="text-base font-semibold">Come valuti la tua esperienza con {inv.shop_name}?</Label>
            <div className="mt-3"><StarInput value={form.rating} onChange={(rating) => setForm({ ...form, rating })} testid="shop-rating-input" /></div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="title">Titolo (facoltativo)</Label>
            <Input id="title" maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="comment">La tua recensione</Label>
            <Textarea id="comment" rows={5} maxLength={3000} placeholder="Racconta spedizione, assistenza, qualità…" value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="review-comment-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="author">Nome pubblico</Label>
            <Input id="author" maxLength={60} value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} data-testid="review-author-input" />
            <p className="text-xs text-gray-500">Mostreremo solo nome e iniziale del cognome.</p>
          </div>
          <div className="space-y-2">
            <Label>Foto e video (facoltativo)</Label>
            <MediaUploader token={token} items={media} onChange={setMedia} testid="review-media" />
            <p className="text-xs text-gray-500">JPG, PNG, WEBP fino a 8 MB · MP4, MOV, WEBM fino a 50 MB · max 10 file in totale.</p>
          </div>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true"
            value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </div>
        {inv.products.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl font-bold tracking-tight">Valuta i prodotti</h2>
            {inv.products.map((p) => (
              <ProductRating key={p.product_id} p={p} token={token} value={prod[p.product_id] || { rating: 0, comment: "" }}
                onChange={(v) => setProd({ ...prod, [p.product_id]: v })}
                onMedia={(fn) => setProd((all) => ({ ...all, [p.product_id]: { ...(all[p.product_id] || { rating: 0, comment: "" }), media: fn(all[p.product_id]?.media || []) } }))} />
            ))}
          </div>
        )}
        <Button type="submit" disabled={sending} className="w-full sm:w-auto rounded-full h-12 px-10 bg-emerald-900 hover:bg-emerald-800 text-base" data-testid="review-submit-button">
          {sending && <Loader2 className="animate-spin mr-2" size={18} />} Invia recensione
        </Button>
      </form>
    </PublicShell>
  );
}
